import type { PasskeyAuthenticationOptions, PasskeyRegistrationOptions } from '@paguz/contracts';
import {
  Button,
  OTPInput,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  toast,
} from '@paguz/ui';
import { FingerprintPatternIcon, MessageSquareIcon, ShieldCheckIcon } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { customerMessage, isApiError } from '../api/errors.ts';
import {
  DEMO_SCA_CODE,
  getPasskeyRegistrationOptions,
  getScaPasskeyOptions,
  registerPasskey,
  verifyScaChallenge,
  verifyScaWithPasskey,
} from '../api/sca.ts';
import { config } from '../config.ts';
import {
  createDevicePasskey,
  deviceHasBiometrics,
  deviceLabel,
  dismissEnrollmentOffer,
  enrollmentOfferDismissed,
  isBiometricCancel,
  localCredentialIds,
  onThisDevice,
  rememberCredential,
  signWithBiometrics,
  webAuthnSupported,
} from '../lib/passkeys.ts';
import { InlineAlert } from './feedback.tsx';

/** Códigos que obligan a empezar la operación de nuevo. */
const TERMINAL_SCA = new Set(['PZ-SCA-003', 'PZ-SCA-404', 'PZ-SCA-005', 'PZ-SCA-006']);

/**
 * - `checking`: ¿hay biometría y passkey en este dispositivo? (se piden las opciones por adelantado
 *   para que el toque del botón abra el diálogo de inmediato, como exige Safari).
 * - `biometric`: "Confirmar con huella o Face ID".
 * - `otp`: código SMS de 6 dígitos (respaldo, o el único método si el dispositivo no tiene biometría).
 * - `enroll`: tras el OTP, ofrecer activar la huella o Face ID para las próximas operaciones.
 */
type Phase = 'checking' | 'biometric' | 'otp' | 'enroll';

interface ScaError {
  message: string;
  terminal: boolean;
}

/** Opciones WebAuthn del reto limitadas a las passkeys de este dispositivo (null → usar OTP). */
async function prepareBiometric(challengeId: string): Promise<PasskeyAuthenticationOptions | null> {
  if (localCredentialIds().length === 0 || !(await deviceHasBiometrics())) return null;
  try {
    const options = onThisDevice(await getScaPasskeyOptions(challengeId));
    return (options.allowCredentials ?? []).length > 0 ? options : null;
  } catch {
    return null;
  }
}

function hasPasskeyHere(): boolean {
  return webAuthnSupported() && localCredentialIds().length > 0;
}

/**
 * Confirmación con autenticación fuerte: **biometría del dispositivo** (passkey: huella, Face ID,
 * Windows Hello) y, si el dispositivo no la tiene o el cliente prefiere, código SMS. Verifica el reto
 * y entrega el `scaToken` al padre, que confirma la operación. El LLM nunca llega aquí: solo el
 * cliente, en la UI.
 */
export function ScaSheet({
  open,
  onOpenChange,
  challengeId,
  title = 'Autoriza esta operación',
  summary,
  onVerified,
  onRestart,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  challengeId: string | null;
  title?: string;
  summary?: ReactNode;
  /** Recibe el `scaToken`; si lanza, se muestra el mensaje del error. */
  onVerified: (scaToken: string) => Promise<void>;
  /** Intentos agotados o reto vencido: el padre reinicia la operación. */
  onRestart?: () => void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!busy) onOpenChange(next);
      }}
    >
      <SheetContent aria-describedby="sca-description" className="mx-auto max-w-[480px]">
        {challengeId === null ? (
          <SheetHeader>
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription id="sca-description">Preparando la verificación…</SheetDescription>
          </SheetHeader>
        ) : (
          <ScaFlow
            key={challengeId}
            challengeId={challengeId}
            title={title}
            summary={summary}
            busy={busy}
            setBusy={setBusy}
            onVerified={onVerified}
            onRestart={onRestart}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function ScaFlow({
  challengeId,
  title,
  summary,
  busy,
  setBusy,
  onVerified,
  onRestart,
}: {
  challengeId: string;
  title: string;
  summary: ReactNode;
  busy: boolean;
  setBusy: (busy: boolean) => void;
  onVerified: (scaToken: string) => Promise<void>;
  onRestart: (() => void) | undefined;
}) {
  const [phase, setPhase] = useState<Phase>(() => (hasPasskeyHere() ? 'checking' : 'otp'));
  /** Opciones listas para firmar (un uso); habilitan "Intentar de nuevo con biometría". */
  const [bioOptions, setBioOptions] = useState<PasskeyAuthenticationOptions | null>(null);
  /** El dispositivo tiene biometría (para ofrecer activarla tras el OTP). */
  const [bioDevice, setBioDevice] = useState(false);
  /** Este dispositivo ya tiene una passkey del cliente para este reto. */
  const [passkeyReady, setPasskeyReady] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<ScaError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [enrollToken, setEnrollToken] = useState<string | null>(null);
  const [enrollOptions, setEnrollOptions] = useState<PasskeyRegistrationOptions | null>(null);

  useEffect(() => {
    let cancelled = false;
    void deviceHasBiometrics().then((available) => {
      if (!cancelled) setBioDevice(available);
    });
    if (hasPasskeyHere())
      void prepareBiometric(challengeId).then((options) => {
        if (cancelled) return;
        setBioOptions(options);
        setPasskeyReady(options !== null);
        setPhase(options === null ? 'otp' : 'biometric');
      });
    return () => {
      cancelled = true;
    };
  }, [challengeId]);

  function failure(err: unknown): ScaError {
    return {
      message: customerMessage(err),
      terminal: isApiError(err) && TERMINAL_SCA.has(err.reasonCode),
    };
  }

  /** Entrega el token al padre (confirma la operación); sus errores se muestran aquí. */
  async function deliver(scaToken: string) {
    setBusy(true);
    try {
      await onVerified(scaToken);
    } catch (err) {
      setError(failure(err));
      setPhase('otp');
    } finally {
      setBusy(false);
    }
  }

  /** Nuevas opciones para reintentar con biometría (el reto WebAuthn anterior ya se usó). */
  function refreshBioOptions() {
    if (!passkeyReady && !hasPasskeyHere()) return;
    void prepareBiometric(challengeId).then(setBioOptions);
  }

  async function confirmWithBiometrics() {
    if (bioOptions === null || busy) return;
    const options = bioOptions;
    setBioOptions(null);
    setBusy(true);
    setError(null);
    setNotice(null);
    let scaToken: string;
    try {
      const assertion = await signWithBiometrics(options);
      ({ scaToken } = await verifyScaWithPasskey(challengeId, assertion));
    } catch (err) {
      setBusy(false);
      setPhase('otp');
      if (isApiError(err)) {
        setError(failure(err));
        if (TERMINAL_SCA.has(err.reasonCode)) return;
      } else {
        setNotice(
          isBiometricCancel(err)
            ? 'No se completó la verificación con huella o Face ID. Escribe el código que te enviamos por SMS o inténtalo de nuevo.'
            : 'No pudimos usar tu huella o Face ID en este momento. Escribe el código que te enviamos por SMS.',
        );
      }
      refreshBioOptions();
      return;
    }
    await deliver(scaToken);
  }

  async function submitOtp(value: string) {
    if (value.length !== 6 || busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    let scaToken: string;
    try {
      ({ scaToken } = await verifyScaChallenge(challengeId, value));
    } catch (err) {
      setCode('');
      setError(failure(err));
      setBusy(false);
      return;
    }
    setCode('');
    if (bioDevice && !passkeyReady && !enrollmentOfferDismissed()) {
      setEnrollToken(scaToken);
      setPhase('enroll');
      setBusy(false);
      getPasskeyRegistrationOptions(challengeId).then(setEnrollOptions, () => {
        // Sin opciones de registro no se ofrece: la operación sigue con el código ya verificado.
        void deliver(scaToken);
      });
      return;
    }
    await deliver(scaToken);
  }

  async function enroll() {
    if (enrollOptions === null || enrollToken === null || busy) return;
    setBusy(true);
    try {
      const registration = await createDevicePasskey(enrollOptions);
      await registerPasskey({
        scaChallengeId: challengeId,
        registration,
        deviceLabel: deviceLabel(),
      });
      rememberCredential(registration.id);
      toast.success('Listo: tus próximas compras las autorizas con tu huella o Face ID.');
    } catch {
      toast.info('No activamos la huella o Face ID. Puedes hacerlo en tu próxima compra.');
    }
    await deliver(enrollToken);
  }

  async function skipEnrollment() {
    if (enrollToken === null || busy) return;
    dismissEnrollmentOffer();
    await deliver(enrollToken);
  }

  const terminal = error?.terminal === true;
  const description: Record<Phase, string> = {
    checking: 'Preparando la verificación…',
    biometric: 'Confirma con tu huella o Face ID para autorizar esta operación.',
    otp: 'Escribe el código de 6 dígitos que te enviamos por SMS para autorizar esta operación.',
    enroll: '¿Quieres autorizar tus próximas compras con tu huella o Face ID?',
  };

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2">
          {phase === 'biometric' || phase === 'enroll' ? (
            <FingerprintPatternIcon className="size-6 text-primary" aria-hidden />
          ) : (
            <ShieldCheckIcon className="size-6 text-primary" aria-hidden />
          )}
          {phase === 'enroll' ? 'Código confirmado' : title}
        </SheetTitle>
        <SheetDescription id="sca-description" aria-live="polite">
          {description[phase]}
        </SheetDescription>
      </SheetHeader>
      <div className="flex flex-col gap-4 px-5 pt-2">
        {phase !== 'enroll' && summary}
        {phase === 'enroll' && (
          <p className="text-sm text-muted-foreground">
            Es más rápido y seguro: tu huella o tu cara nunca salen de tu teléfono. Si algún día no
            funciona, te enviamos el código por SMS.
          </p>
        )}
        {phase === 'checking' && (
          <p role="status" className="text-sm text-muted-foreground">
            Buscando la huella o Face ID de este dispositivo…
          </p>
        )}
        {phase === 'otp' && (
          <>
            {notice !== null && <InlineAlert tone="info">{notice}</InlineAlert>}
            {config.demoMode && (
              <InlineAlert tone="info">
                Código de demostración: <strong className="tracking-widest">{DEMO_SCA_CODE}</strong>
              </InlineAlert>
            )}
            <OTPInput
              value={code}
              onChange={(value: string) => {
                setCode(value);
                if (value.length === 6) void submitOtp(value);
              }}
              disabled={busy || terminal}
              invalid={error !== null && !terminal}
              autoFocus
              aria-label="Código de verificación"
            />
          </>
        )}
        {error !== null && (
          <InlineAlert
            tone="danger"
            action={
              terminal && onRestart ? (
                <Button variant="outline" size="sm" className="self-start" onClick={onRestart}>
                  Empezar de nuevo
                </Button>
              ) : undefined
            }
          >
            {error.message}
          </InlineAlert>
        )}
      </div>
      <SheetFooter>
        {phase === 'checking' && (
          <Button size="lg" loading disabled>
            Preparando…
          </Button>
        )}
        {phase === 'biometric' && (
          <>
            <Button
              size="lg"
              loading={busy}
              disabled={bioOptions === null}
              onClick={() => void confirmWithBiometrics()}
            >
              <FingerprintPatternIcon aria-hidden />
              {busy ? 'Confirmando…' : 'Confirmar con huella o Face ID'}
            </Button>
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => {
                setPhase('otp');
                setNotice(null);
              }}
            >
              <MessageSquareIcon aria-hidden />
              Usar código SMS
            </Button>
          </>
        )}
        {phase === 'otp' && (
          <>
            <Button
              size="lg"
              loading={busy}
              disabled={code.length !== 6 || terminal}
              onClick={() => void submitOtp(code)}
            >
              {busy ? 'Confirmando…' : 'Confirmar'}
            </Button>
            {passkeyReady && !terminal && (
              <Button
                variant="ghost"
                disabled={busy || bioOptions === null}
                onClick={() => void confirmWithBiometrics()}
              >
                <FingerprintPatternIcon aria-hidden />
                Intentar de nuevo con biometría
              </Button>
            )}
          </>
        )}
        {phase === 'enroll' && (
          <>
            <Button
              size="lg"
              loading={busy || enrollOptions === null}
              onClick={() => void enroll()}
            >
              <FingerprintPatternIcon aria-hidden />
              Activar huella o Face ID
            </Button>
            <Button variant="ghost" disabled={busy} onClick={() => void skipEnrollment()}>
              Ahora no
            </Button>
          </>
        )}
      </SheetFooter>
    </>
  );
}
